import classNames from "classnames";
import {
  centsToDollarString,
  convertDateStringToMonthDayYearFormat,
  formatInCentralTime,
} from "../../../lib/formatting";
import styles from "./NotesDetails.module.css";
import { Col, Row } from "react-grid-system";
import { useGetNoteDetails } from "../../../lib/api/queries";
import Spinner from "../../common/Spinner";
import StyledError from "../../common/StyledError";

const strings = {
  failedToLoad: "Failed to load notes details",
  accountStatusDate: (date: string) => `Status of Account as of ${date}`,
  agentLabel: "Agent:",
  agentValue: (agentCode: string, firstName: string, lastName: string) =>
    `${agentCode} / ${lastName}, ${firstName}`,
  origAcctLabel: "Orig Acct:",
  origAcctValue: (acct: string) => `x${acct}`,
  intExtStatusLabel: "Int/Ext Status:",
  delqBucketLabel: "Delq Bucket",
  numDaysDelqLabel: "# Days Deliq",
  pricingControlCureLabel: "Pricing/Control/Cure:",
  cycleCodeLabel: "Cycle Cd:",
  paymentDueDateLabel: "Pmt Due Dt:",
  nextCycleStatementDateLabel: "Next Cycle/Stmt Dt:",
  permCollectorCodeLabel: "Perm Coll Cd:",
  tempCollectorCodeLabel: "Temp Coll Cd:",
  behaviorCreditBureauScoreLabel: "Score BH/CB:",
  totalAmountDueLabel: "Total Amt Due:",
  oclAmountLabel: "OCL Amt:",
  amountDueLabel: "Amt Due:",
  amountDelinquentLabel: "Amt Delq:",
  currentBalanceLabel: "Current Bal:",
  currentMonthlyPaymentLabel: "Current Mo Pmt:",
  fixedPaymentAmountLabel: "Fix Pmt Amt:",
  creditLimitLabel: "Credit Limit:",
  delinquentBucketsLabel: "Delq Bucket 1-7",
  delinquentBucket: (amount: number | null) =>
    `${amount !== null ? centsToDollarString(amount, true) : "-"}`,
  noteContent: "Content",
};

type NoteDetailsRowProps = {
  label: string;
  value: string;
  valueClassName?: string;
};

function NoteDetailsRow({ label, value, valueClassName }: NoteDetailsRowProps) {
  return (
    <div className={classNames(styles.noteDetailRow)}>
      {label}
      <div className={classNames(styles.noteDetailsValue, valueClassName)}>
        {value}
      </div>
    </div>
  );
}

type Props = {
  dwbuid: string;
  noteId: string;
  noteContent: string;
  noteDate: string;
  accountNumberLast: string;
};

export default function NotesDetails({
  dwbuid,
  noteId,
  noteContent,
  noteDate,
  accountNumberLast,
}: Props) {
  const {
    data: getNoteDetailsData,
    isLoading: isNotesDetailsLoading,
    isError: isNotesDetailsError,
    error: getNotesDetailsError,
    refetch: getNotesDetailsRefetch,
  } = useGetNoteDetails(dwbuid, noteId);

  return isNotesDetailsLoading ? (
    <Row>
      <Col className={styles.spinnerContainer}>
        <Spinner />
      </Col>
    </Row>
  ) : isNotesDetailsError ? (
    <Row>
      <Col>
        <StyledError
          errorTitle={strings.failedToLoad}
          error={getNotesDetailsError}
          refetch={getNotesDetailsRefetch}
        />
      </Col>
    </Row>
  ) : (
    <>
      <Row>
        <Col md={12}>
          <div className={styles.topDetailsRow}>
            {strings.accountStatusDate(
              formatInCentralTime(new Date(noteDate), "MM/dd/yyyy HH:mm"),
            )}
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={3}>
          <NoteDetailsRow
            label={strings.agentLabel}
            value={strings.agentValue(
              getNoteDetailsData.accountContent.agentFdrCode,
              getNoteDetailsData.accountContent.agentFirstName,
              getNoteDetailsData.accountContent.agentLastName,
            )}
            valueClassName={styles.truncateOnWrap}
          />
          <NoteDetailsRow
            label={strings.origAcctLabel}
            value={strings.origAcctValue(accountNumberLast)}
          />
          <NoteDetailsRow
            label={strings.intExtStatusLabel}
            value={getNoteDetailsData.accountContent.internalExternalStatus}
          />
          <NoteDetailsRow
            label={strings.delqBucketLabel}
            value={getNoteDetailsData.accountContent.delinquentBucket}
          />
          <NoteDetailsRow
            label={strings.numDaysDelqLabel}
            value={getNoteDetailsData.accountContent.daysDelinquent.toString()}
          />
        </Col>
        <Col md={3}>
          <NoteDetailsRow
            label={strings.pricingControlCureLabel}
            value={`${getNoteDetailsData.accountContent.pricing ?? "--"} / ${getNoteDetailsData.accountContent.unitPricingControl12 ?? "--"} / ${getNoteDetailsData.accountContent.cureIndicator ?? "--"}`}
          />
          <NoteDetailsRow
            label={strings.cycleCodeLabel}
            value={`${getNoteDetailsData.accountContent.cycleCode} / ${getNoteDetailsData.accountContent.paperlessDescription ?? "--"}`}
          />
          <NoteDetailsRow
            label={strings.paymentDueDateLabel}
            value={convertDateStringToMonthDayYearFormat(
              getNoteDetailsData.accountContent.paymentDueDate,
            )}
          />
          <NoteDetailsRow
            label={strings.nextCycleStatementDateLabel}
            value={convertDateStringToMonthDayYearFormat(
              getNoteDetailsData.accountContent.nextCycleDate,
            )}
          />
          <NoteDetailsRow
            label={strings.permCollectorCodeLabel}
            value={getNoteDetailsData.accountContent.permCollectorCode ?? ""}
          />
        </Col>
        <Col md={3}>
          <NoteDetailsRow
            label={strings.tempCollectorCodeLabel}
            value={getNoteDetailsData.accountContent.tempCollectorCode ?? ""}
          />
          <NoteDetailsRow
            label={strings.behaviorCreditBureauScoreLabel}
            value={getNoteDetailsData.accountContent.behaviorCreditBureauScore}
          />
          <NoteDetailsRow
            label={strings.totalAmountDueLabel}
            value={centsToDollarString(
              getNoteDetailsData.accountContent.totalAmountDueCents,
              true,
            )}
          />
          <NoteDetailsRow
            label={strings.oclAmountLabel}
            value={centsToDollarString(
              getNoteDetailsData.accountContent.oclAmountCents,
              true,
            )}
          />
          <NoteDetailsRow
            label={strings.amountDueLabel}
            value={centsToDollarString(
              getNoteDetailsData.accountContent.amountDueCents,
              true,
            )}
          />
        </Col>
        <Col md={3}>
          <NoteDetailsRow
            label={strings.amountDelinquentLabel}
            value={centsToDollarString(
              getNoteDetailsData.accountContent.amountDelinquentCents,
              true,
              true,
            )}
          />
          <NoteDetailsRow
            label={strings.currentBalanceLabel}
            value={centsToDollarString(
              getNoteDetailsData.accountContent.currentBalanceCents,
              true,
            )}
          />
          <NoteDetailsRow
            label={strings.currentMonthlyPaymentLabel}
            value={centsToDollarString(
              getNoteDetailsData.accountContent.currentMonthlyPaymentCents,
              true,
            )}
          />
          <NoteDetailsRow
            label={strings.fixedPaymentAmountLabel}
            value={centsToDollarString(
              getNoteDetailsData.accountContent.fixedPaymentAmountCents,
              true,
            )}
          />
          <NoteDetailsRow
            label={strings.creditLimitLabel}
            value={centsToDollarString(
              getNoteDetailsData.accountContent.creditLimitCents,
              true,
            ).slice(0, -3)}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <div className={styles.delinquentRow}>
            {strings.delinquentBucketsLabel}
            <ol className={styles.delinquentBuckets}>
              <li>
                {strings.delinquentBucket(
                  getNoteDetailsData.accountContent.delinquentBucket1Cents,
                )}
              </li>
              <li>
                {strings.delinquentBucket(
                  getNoteDetailsData.accountContent.delinquentBucket2Cents,
                )}
              </li>
              <li>
                {strings.delinquentBucket(
                  getNoteDetailsData.accountContent.delinquentBucket3Cents,
                )}
              </li>
              <li>
                {strings.delinquentBucket(
                  getNoteDetailsData.accountContent.delinquentBucket4Cents,
                )}
              </li>
              <li>
                {strings.delinquentBucket(
                  getNoteDetailsData.accountContent.delinquentBucket5Cents,
                )}
              </li>
              <li>
                {strings.delinquentBucket(
                  getNoteDetailsData.accountContent.delinquentBucket6Cents,
                )}
              </li>
              <li>
                {strings.delinquentBucket(
                  getNoteDetailsData.accountContent.delinquentBucket7Cents,
                )}
              </li>
            </ol>
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <div className={styles.noteContentContainer}>
            {strings.noteContent}
            <div className={styles.noteContent}>{noteContent}</div>
          </div>
        </Col>
      </Row>
    </>
  );
}
